import { Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { HttpErrorResponse } from "@angular/common/http";
import { CompanyService } from "./company.service";
import {
  AbstractControl,
  FormControl,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { ToastrService } from "../shared/toastr.service";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { GetCompanySettingsAction } from "../settings/settings.actions";
import {
  AdvancedSettings,
  CompanySettings,
} from "../settings/settings.interface";
import { validateNonRequiredPhoneNumber } from "../shared/validators/phone-number.validator";
import {
  ALLOWED_RASTER_IMAGE_MIME_TYPES,
  Image,
} from "../shared/image-uploader/image.interface";
import { ImageService } from "../shared/image-uploader/image.service";
import { ALLOWED_BRAND_FONT_EXTENSIONS } from "./company.interface";
import { ReinstallSignaturesAction } from "../signatures/signatures.actions";
import { StartCheckingForInstallationInProgressAction } from "../signature-installation-state/signature-installation-state.actions";
import { AdvancedSettingsService } from "../advanced-settings/advanced-settings.service";
import { SettingsService } from "../settings/settings.service";
import { EmployeesService } from "../employees/employees.service";
import { EmployeePickerItem } from "../employees/employees.interface";
import {
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
} from "@angular/material/autocomplete";
import {
  Observable,
  Subject,
  Subscription,
  forkJoin,
  of,
  firstValueFrom,
} from "rxjs";
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  finalize,
  map,
  switchMap,
} from "rxjs/operators";
import { ShowOnSubmitAttemptErrorStateMatcher } from "../shared/error-state-matcher";
import type { PrintCardOrderApproverOption } from "./company.interface";

const SEARCH_DEBOUNCE_MS = 280;
const SEARCH_PAGE_SIZE = 50;

@Component({
  selector: "app-company",
  templateUrl: "./company.component.html",
  styleUrls: ["./company.component.scss"],
  standalone: false,
})
export class CompanyComponent implements OnInit, OnDestroy {
  loading = true;
  saving = false;
  uploadingFont = false;
  readonly brandFontAccept = ALLOWED_BRAND_FONT_EXTENSIONS.map(
    (ext) => `.${ext}`,
  ).join(",");
  readonly rasterMimeTypes = ALLOWED_RASTER_IMAGE_MIME_TYPES;
  hasChanges = false;
  errorStateMatcher = new ShowOnSubmitAttemptErrorStateMatcher();
  private initialFormValue: string | null = null;
  file: File | null = null;
  previewUrl: string | undefined;
  private photoRemoved = false;
  company: CompanySettings | null = null;
  brandFontFileUrl: string | null = null;
  brandFontFileName: string | null = null;
  private currentAdvancedSettings: AdvancedSettings | null = null;
  private advancedSettingsLoaded = false;

  contactPersonSearchCtrl = new FormControl("", { nonNullable: true });
  contactPersonOptions: EmployeePickerItem[] = [];
  selectedContactPerson: EmployeePickerItem | null = null;
  private readonly contactPersonQuery$ = new Subject<string | undefined>();
  @ViewChild("contactPersonAutoTrigger")
  contactPersonAutoTrigger?: MatAutocompleteTrigger;

  printCardOrderApprovers: PrintCardOrderApproverOption[] = [];
  filteredPrintCardOrderApprovers: PrintCardOrderApproverOption[] = [];
  printCardOrderApproversLoading = false;
  printCardOrderApproversLoaded = false;
  approverSearchCtrl = new FormControl("", { nonNullable: true });
  selectedApprover: PrintCardOrderApproverOption | null = null;
  @ViewChild("approverAutoTrigger")
  approverAutoTrigger?: MatAutocompleteTrigger;

  private readonly sub = new Subscription();

  form = new UntypedFormGroup({
    name: new UntypedFormControl(),
    website: new UntypedFormControl(),
    address: new UntypedFormControl(),
    phone: new UntypedFormControl("", [validateNonRequiredPhoneNumber]),
    contactPersonEmail: new UntypedFormControl("", [
      (c: AbstractControl) => Validators.email(c),
    ]),
    brandFontFamily: new UntypedFormControl(""),
    imageWidth: new UntypedFormControl("", Validators.min(0)),
    imageHeight: new UntypedFormControl("", Validators.min(0)),
    originalImageWidth: new UntypedFormControl("", Validators.min(0)),
    originalImageHeight: new UntypedFormControl("", Validators.min(0)),
    printCardOrderApprover: new UntypedFormControl(""),
  });

  constructor(
    private service: CompanyService,
    private toastr: ToastrService,
    private translate: TranslateService,
    private store: Store,
    public imageService: ImageService,
    private advancedSettingsService: AdvancedSettingsService,
    private settingsService: SettingsService,
    private employeesService: EmployeesService,
  ) {}

  ngOnInit(): void {
    this.setupContactPersonSearch();
    this.setupApproverSearch();
    this.loading = true;
    this.sub.add(
      forkJoin({
        company: this.service.get(),
        advanced: this.advancedSettingsService.get().pipe(
          catchError(() => {
            this.toastr.error(
              this.translate.instant("company.saveFailed") as string,
            );
            return of<AdvancedSettings | null>(null);
          }),
        ),
        approvers: this.settingsService
          .listPrintCardOrderApprovers()
          .pipe(
            catchError(() =>
              of({ approvers: [] as PrintCardOrderApproverOption[] }),
            ),
          ),
      }).subscribe({
        next: ({ company, advanced, approvers }) => {
          const contactPersonEmail =
            company.contactPersonEmail?.trim().toLowerCase() || "";
          this.form.patchValue(
            {
              name: company.name,
              website: company.website,
              address: company.address,
              phone: company.phone,
              contactPersonEmail,
              brandFontFamily: company.brandFontFamily || "",
              imageWidth: company.imageWidth,
              imageHeight: company.imageHeight,
              originalImageWidth: company.originalImageWidth,
              originalImageHeight: company.originalImageHeight,
            },
            { emitEvent: false },
          );
          if (!this.photoRemoved) {
            this.previewUrl = company.photoUrl
              ? `${company.photoUrl}?t=${Date.now()}`
              : undefined;
          }
          this.brandFontFileUrl =
            typeof company.brandFontFileUrl === "string" &&
            company.brandFontFileUrl.trim().length > 0
              ? company.brandFontFileUrl.trim()
              : null;
          this.brandFontFileName =
            typeof company.brandFontFileName === "string" &&
            company.brandFontFileName.trim().length > 0
              ? company.brandFontFileName.trim()
              : null;
          this.company = company;
          if (contactPersonEmail) {
            this.contactPersonSearchCtrl.setValue(contactPersonEmail, {
              emitEvent: false,
            });
            this.contactPersonQuery$.next(contactPersonEmail);
          } else {
            this.selectedContactPerson = null;
            this.contactPersonSearchCtrl.setValue("", { emitEvent: false });
          }

          this.store.dispatch(new GetCompanySettingsAction());
          this.advancedSettingsLoaded = true;
          if (advanced) {
            this.currentAdvancedSettings = advanced;
            const approver =
              advanced.printCardOrderApprover?.trim().toLowerCase() || "";
            this.form.patchValue(
              { printCardOrderApprover: approver },
              { emitEvent: false },
            );
            if (approver) {
              this.approverSearchCtrl.setValue(approver, { emitEvent: false });
            } else {
              this.selectedApprover = null;
              this.approverSearchCtrl.setValue("", { emitEvent: false });
            }
          }

          this.printCardOrderApprovers = (approvers.approvers ?? []).map(
            (row) => ({
              email: row.email,
              role: row.role,
              givenName: row.givenName,
              familyName: row.familyName,
              fullName: row.fullName,
            }),
          );
          this.printCardOrderApproversLoaded = true;
          this.filterApprovers(this.approverSearchCtrl.value);
          this.tryResolveSelectedApprover();
          this.captureInitialFormValue();
          this.loading = false;
        },
        error: () => {
          this.loading = false;
          this.toastr.error(
            this.translate.instant("company.saveFailed") as string,
          );
        },
      }),
    );

    this.sub.add(
      this.form.valueChanges.subscribe(() => {
        this.checkForChanges();
      }),
    );
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  private setupContactPersonSearch(): void {
    this.sub.add(
      this.contactPersonQuery$
        .pipe(
          distinctUntilChanged(),
          switchMap((phrase) => this.fetchContactPersonEmployees(phrase)),
        )
        .subscribe((rows) => {
          this.contactPersonOptions = rows;
          this.tryResolveSelectedContactPerson(rows);
        }),
    );
    this.sub.add(
      this.contactPersonSearchCtrl.valueChanges
        .pipe(debounceTime(SEARCH_DEBOUNCE_MS))
        .subscribe((value) =>
          this.contactPersonQuery$.next(
            this.contactPersonQueryFromInput(value),
          ),
        ),
    );
  }

  private setupApproverSearch(): void {
    this.sub.add(
      this.approverSearchCtrl.valueChanges
        .pipe(debounceTime(SEARCH_DEBOUNCE_MS))
        .subscribe((value) => this.filterApprovers(value)),
    );
  }

  loadPrintCardOrderApprovers(): void {
    if (
      this.printCardOrderApproversLoaded ||
      this.printCardOrderApproversLoading
    ) {
      return;
    }
    this.printCardOrderApproversLoading = true;
    this.settingsService.listPrintCardOrderApprovers().subscribe({
      next: (res) => {
        this.printCardOrderApprovers = res.approvers ?? [];
        this.printCardOrderApproversLoaded = true;
        this.printCardOrderApproversLoading = false;
        this.filterApprovers(this.approverSearchCtrl.value);
        this.tryResolveSelectedApprover();
      },
      error: () => {
        this.printCardOrderApprovers = [];
        this.filteredPrintCardOrderApprovers = [];
        this.printCardOrderApproversLoading = false;
        this.toastr.error(
          this.translate.instant(
            "company.printCardOrderApproversLoadFailed",
          ) as string,
        );
      },
    });
  }

  displayEmployeeOption(employee: EmployeePickerItem): string {
    const name = this.employeeFullName(employee);
    const email = employee.email?.trim() || "";
    if (name && email) {
      return `${name} (${email})`;
    }
    return email || name;
  }

  displayApproverOption(approver: PrintCardOrderApproverOption): string {
    const name = approver.fullName?.trim();
    return name ? `${name} (${approver.email})` : approver.email;
  }

  employeeFullName(employee: EmployeePickerItem): string {
    return (
      employee.fullName?.trim() ||
      `${employee.givenName ?? ""} ${employee.familyName ?? ""}`.trim() ||
      ""
    );
  }

  employeeEmail(employee: EmployeePickerItem): string {
    return employee.email?.trim() || "";
  }

  contactPersonOptionTrackBy = (_: number, item: EmployeePickerItem): string =>
    item.employeeId;

  approverOptionTrackBy = (
    _: number,
    item: PrintCardOrderApproverOption,
  ): string => item.email;

  onContactPersonTriggerClick(): void {
    if (this.loading) {
      return;
    }
    if (this.contactPersonSearchCtrl.value && this.selectedContactPerson) {
      this.contactPersonSearchCtrl.setValue("", { emitEvent: true });
    } else {
      this.contactPersonQuery$.next(
        this.contactPersonQueryFromInput(this.contactPersonSearchCtrl.value),
      );
    }
    this.contactPersonAutoTrigger?.openPanel();
  }

  onContactPersonBlur(): void {
    if (!this.selectedContactPerson) {
      if (!this.form.get("contactPersonEmail")?.value) {
        this.contactPersonSearchCtrl.setValue("", { emitEvent: false });
      }
      return;
    }
    const expected = this.displayEmployeeOption(this.selectedContactPerson);
    if (this.contactPersonSearchCtrl.value?.trim() !== expected) {
      this.contactPersonSearchCtrl.setValue(expected, { emitEvent: false });
    }
  }

  onContactPersonSelected(event: MatAutocompleteSelectedEvent): void {
    const value = event.option.value as EmployeePickerItem | "";
    if (value === "") {
      this.clearContactPerson();
      return;
    }
    this.selectContactPerson(value);
  }

  clearContactPerson(): void {
    this.selectedContactPerson = null;
    this.contactPersonSearchCtrl.setValue("", { emitEvent: false });
    this.form.patchValue({ contactPersonEmail: "" });
    this.contactPersonQuery$.next(undefined);
  }

  onApproverTriggerClick(): void {
    if (this.loading) {
      return;
    }
    this.loadPrintCardOrderApprovers();
    if (this.approverSearchCtrl.value && this.selectedApprover) {
      this.approverSearchCtrl.setValue("", { emitEvent: true });
    } else {
      this.filterApprovers(this.approverSearchCtrl.value);
    }
    this.approverAutoTrigger?.openPanel();
  }

  onApproverBlur(): void {
    if (!this.selectedApprover) {
      if (!this.form.get("printCardOrderApprover")?.value) {
        this.approverSearchCtrl.setValue("", { emitEvent: false });
      }
      return;
    }
    const expected = this.displayApproverOption(this.selectedApprover);
    if (this.approverSearchCtrl.value?.trim() !== expected) {
      this.approverSearchCtrl.setValue(expected, { emitEvent: false });
    }
  }

  onApproverSelected(event: MatAutocompleteSelectedEvent): void {
    const value = event.option.value as PrintCardOrderApproverOption | "";
    if (value === "") {
      this.clearApprover();
      return;
    }
    this.selectApprover(value);
  }

  clearApprover(): void {
    this.selectedApprover = null;
    this.approverSearchCtrl.setValue("", { emitEvent: false });
    this.form.patchValue({ printCardOrderApprover: "" });
    this.filterApprovers("");
  }

  private selectContactPerson(employee: EmployeePickerItem): void {
    const email = this.employeeEmail(employee).toLowerCase();
    this.selectedContactPerson = employee;
    this.contactPersonSearchCtrl.setValue(
      this.displayEmployeeOption(employee),
      {
        emitEvent: false,
      },
    );
    this.form.patchValue({ contactPersonEmail: email });
  }

  private selectApprover(approver: PrintCardOrderApproverOption): void {
    this.selectedApprover = approver;
    this.approverSearchCtrl.setValue(this.displayApproverOption(approver), {
      emitEvent: false,
    });
    this.form.patchValue({
      printCardOrderApprover: approver.email.trim().toLowerCase(),
    });
  }

  private tryResolveSelectedContactPerson(rows: EmployeePickerItem[]): void {
    if (this.selectedContactPerson) {
      return;
    }
    const selected =
      (this.form.get("contactPersonEmail")?.value as string | null | undefined)
        ?.trim()
        .toLowerCase() || "";
    if (!selected) {
      return;
    }
    const match = rows.find(
      (row) => this.employeeEmail(row).toLowerCase() === selected,
    );
    if (match) {
      this.selectContactPerson(match);
      return;
    }
    this.selectedContactPerson = {
      employeeId: selected,
      email: selected,
    };
    this.contactPersonSearchCtrl.setValue(selected, { emitEvent: false });
  }

  private tryResolveSelectedApprover(): void {
    const selected =
      (
        this.form.get("printCardOrderApprover")?.value as
          string | null | undefined
      )
        ?.trim()
        .toLowerCase() || "";
    if (!selected) {
      return;
    }
    const match = this.printCardOrderApprovers.find(
      (approver) => approver.email === selected,
    );
    if (match) {
      this.selectApprover(match);
      return;
    }
    if (!this.selectedApprover) {
      this.selectedApprover = {
        email: selected,
        role: "",
        givenName: null,
        familyName: null,
        fullName: null,
      };
      this.approverSearchCtrl.setValue(selected, { emitEvent: false });
    }
  }

  private filterApprovers(raw: string | null | undefined): void {
    const query = (raw ?? "").trim().toLowerCase();
    const selectedLabel = this.selectedApprover
      ? this.displayApproverOption(this.selectedApprover).toLowerCase()
      : "";
    const phrase =
      !query || (this.selectedApprover && query === selectedLabel) ? "" : query;
    if (!phrase) {
      this.filteredPrintCardOrderApprovers = [...this.printCardOrderApprovers];
      return;
    }
    this.filteredPrintCardOrderApprovers = this.printCardOrderApprovers.filter(
      (approver) => {
        const name = (approver.fullName || "").toLowerCase();
        return name.includes(phrase) || approver.email.includes(phrase);
      },
    );
  }

  private fetchContactPersonEmployees(
    phrase: string | undefined,
  ): Observable<EmployeePickerItem[]> {
    return this.employeesService
      .getEmployeesPicker({
        pager: { page: 0, limit: SEARCH_PAGE_SIZE },
        phrase,
      })
      .pipe(
        map((res) => (Array.isArray(res.data) ? res.data : [])),
        catchError(() => of([] as EmployeePickerItem[])),
      );
  }

  private contactPersonQueryFromInput(
    raw: string | null | undefined,
  ): string | undefined {
    const value = (raw ?? "").trim();
    if (!value) {
      return undefined;
    }
    if (
      this.selectedContactPerson &&
      value === this.displayEmployeeOption(this.selectedContactPerson)
    ) {
      return undefined;
    }
    return value;
  }

  save = async () => {
    this.errorStateMatcher.submitAttempted = true;
    if (this.form.valid) {
      this.saving = true;
      try {
        const formValue = this.form.value as CompanySettings & {
          printCardOrderApprover?: string | null;
        };
        const {
          printCardOrderApprover: printCardOrderApproverRaw,
          contactPersonEmail: contactPersonEmailRaw,
          ...companyRest
        } = formValue;
        const contactPersonEmail =
          (contactPersonEmailRaw || "").toLowerCase() || null;
        const companyPayload = {
          ...companyRest,
          name: companyRest.name || null,
          website: companyRest.website || null,
          address: companyRest.address || null,
          phone: companyRest.phone || null,
          brandFontFamily: companyRest.brandFontFamily || null,
          contactPersonEmail,
          brandFontFileUrl: this.brandFontFileUrl || null,
          brandFontFileName: this.brandFontFileName || null,
          photoUrl:
            this.photoRemoved && !this.file
              ? null
              : this.company?.photoUrl || null,
        };
        await firstValueFrom(this.service.save(companyPayload));
        if (this.file) {
          await firstValueFrom(this.service.upload(this.file));
        }
        this.photoRemoved = false;

        const approverEmail = (printCardOrderApproverRaw || "").toLowerCase();
        const currentApprover =
          this.currentAdvancedSettings?.printCardOrderApprover
            ?.trim()
            .toLowerCase() || "";
        if (approverEmail !== currentApprover) {
          if (!this.currentAdvancedSettings) {
            this.currentAdvancedSettings = await firstValueFrom(
              this.advancedSettingsService.get(),
            );
          }
          const settings = this.currentAdvancedSettings;
          const updated = await firstValueFrom(
            this.advancedSettingsService.update({
              ...settings,
              printCardOrderApprover: approverEmail || null,
            }),
          );
          this.currentAdvancedSettings = {
            ...settings,
            printCardOrderApprover: approverEmail || null,
            ...(updated as Partial<AdvancedSettings>),
          };
        }

        this.store.dispatch(new StartCheckingForInstallationInProgressAction());
        this.store.dispatch(new ReinstallSignaturesAction());
        this.store.dispatch(new GetCompanySettingsAction());

        this.toastr.success(
          this.translate.instant("settings.companyDataEditSuccess") as string,
        );
        this.hasChanges = false;
        this.initialFormValue = JSON.stringify(this.form.value);
      } catch (error) {
        if (
          error instanceof HttpErrorResponse &&
          (error.error as { message?: string })?.message ===
            "PRINT_CARD_ORDER_APPROVER_INVALID"
        ) {
          this.toastr.error(
            this.translate.instant(
              "company.printCardOrderApproverInvalid",
            ) as string,
          );
        } else {
          this.toastr.error(
            this.translate.instant("company.saveFailed") as string,
          );
        }
      } finally {
        this.saving = false;
      }
    }
  };

  private captureInitialFormValue(): void {
    if (!this.company || !this.advancedSettingsLoaded) {
      return;
    }
    this.initialFormValue = JSON.stringify(this.form.value);
    this.hasChanges = false;
  }

  private checkForChanges(): void {
    if (this.initialFormValue) {
      this.hasChanges =
        this.initialFormValue !== JSON.stringify(this.form.value);
    }
  }

  fileChange = (image: Image) => {
    const formValue = this.form.value as CompanySettings;
    const width = image.width || formValue.originalImageWidth;
    const height = image.height || formValue.originalImageHeight;

    this.form.patchValue({
      originalImageWidth: width,
      originalImageHeight: height,
      imageWidth: width,
      imageHeight: height,
    });
    this.previewUrl = image.previewUrl;
    this.file = image.file;
    this.photoRemoved = false;
    this.hasChanges = true;
  };

  removePhoto = () => {
    this.file = null;
    this.previewUrl = undefined;
    this.photoRemoved = true;
    this.hasChanges = true;
  };

  onBrandFontFileChange(files: FileList | null): void {
    const file = files?.[0];
    if (!file) {
      return;
    }
    const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
    if (!(ALLOWED_BRAND_FONT_EXTENSIONS as readonly string[]).includes(ext)) {
      this.toastr.error(
        this.translate.instant("company.fontWrongType") as string,
      );
      return;
    }
    if (file.size > 5_000_000) {
      this.toastr.error(
        this.translate.instant("company.fileTooLarge") as string,
      );
      return;
    }
    this.uploadingFont = true;
    this.service
      .uploadFont(file)
      .pipe(finalize(() => (this.uploadingFont = false)))
      .subscribe({
        next: (result) => {
          this.brandFontFileUrl = result.fontUrl?.trim() || null;
          this.brandFontFileName = result.fontFileName?.trim() || file.name;
          if (!(this.form.get("brandFontFamily")?.value as string)?.trim()) {
            this.form.patchValue({ brandFontFamily: result.fontFamily });
          }
          this.store.dispatch(new GetCompanySettingsAction());
          this.hasChanges = true;
          this.toastr.success(
            this.translate.instant("company.fontUploaded") as string,
          );
        },
        error: () => {
          this.toastr.error(
            this.translate.instant("company.fontUploadFailed") as string,
          );
        },
      });
  }

  clearBrandFont(): void {
    this.uploadingFont = true;
    this.service
      .clearFont()
      .pipe(finalize(() => (this.uploadingFont = false)))
      .subscribe({
        next: () => {
          this.brandFontFileUrl = null;
          this.brandFontFileName = null;
          this.form.patchValue({ brandFontFamily: "" });
          this.store.dispatch(new GetCompanySettingsAction());
          this.hasChanges = true;
          this.toastr.success(
            this.translate.instant("company.fontRemoved") as string,
          );
        },
        error: () => {
          this.toastr.error(
            this.translate.instant("company.fontRemoveFailed") as string,
          );
        },
      });
  }
}
